import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory 
        ? `${API}/products?category=${selectedCategory}`
        : `${API}/products`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory pt-24" data-testid="products-page">
      {/* Hero Banner */}
      <div className="bg-beige py-16 md:py-24 mb-12">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <span className="overline mb-3 block">Explore</span>
          <h1 className="font-heading text-4xl md:text-6xl font-light text-charcoal" data-testid="products-title">
            Collections
          </h1>
          <p className="text-charcoal-light mt-4 max-w-lg">
            Discover our curated selection of premium men's fashion, crafted with exceptional quality.
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pb-24">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Filters Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="sticky top-28" data-testid="filters-sidebar">
              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full lg:hidden flex items-center justify-between py-4 border-b border-charcoal/10 mb-4"
              >
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-gold" strokeWidth={1.5} />
                  <span className="text-xs tracking-[0.15em] uppercase text-charcoal">Filters</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-charcoal transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>

              <div className={`${showFilters ? 'block' : 'hidden'} lg:block`}>
                <div className="mb-8">
                  <h3 className="text-xs tracking-[0.15em] uppercase text-charcoal-light mb-4">Category</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setSelectedCategory('')}
                      className={`block w-full text-left text-sm py-2 transition-colors ${
                        selectedCategory === '' 
                          ? 'text-gold font-medium' 
                          : 'text-charcoal hover:text-gold'
                      }`}
                      data-testid="filter-all"
                    >
                      All Products
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`block w-full text-left text-sm py-2 transition-colors ${
                          selectedCategory === cat.id 
                            ? 'text-gold font-medium' 
                            : 'text-charcoal hover:text-gold'
                        }`}
                        data-testid={`filter-${cat.id}`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-charcoal/10 pt-6">
                  <p className="text-xs text-charcoal-light">
                    {products.length} {products.length === 1 ? 'Product' : 'Products'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[3/4] bg-beige mb-4" />
                    <div className="h-4 bg-beige w-3/4 mb-2" />
                    <div className="h-4 bg-beige w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-charcoal-light text-lg">No products found in this category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="products-grid">
                {products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
